import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useAssets } from '../data/fleet.ts'
import {
  INCIDENT_DESCRIPTION_MAX, INCIDENT_KINDS, INCIDENT_SEVERITIES, buildReportPayload, codePoints, kindText, reportProblem, responsibleOptions, severityText,
  useIncidentsWaiting, useReportState, type IncidentKind, type IncidentSeverity, type ReportProblem,
} from '../data/incidents.ts'
import { useMemberships, usePersons } from '../data/people.ts'
import { machineLabel } from '../data/planUi.ts'
import { formatLocal, localToInstant } from '../lib/time.ts'
import { PhotoField } from '../photos/PhotoField.tsx'
import { usePhotoDraft } from '../photos/usePhotos.ts'
import { BackLink, Banner, DisabledHint, Field, errorText } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { useAreaOptions } from './measures/shared.tsx'

/** "Signaler un incident" (benchmark 63, GK-044; docs/M16.md G): an injury, a near miss, damage, a chemical or environmental event, reported by anyone who works,
 *  even with no signal (a field FACT and a safety fact: incident.report@1, offline-first, salvaged if it fails). Reached from Today's Terrain row (the shared
 *  workstation included, under the person's PIN). The form asks for the facts only: what happened, how serious, where, with which machine, who was there (names
 *  only), when, a photo — and says it asks no health detail. Until the club's adviser has answered, it carries the line "Un accident corporel se déclare aussi
 *  dans le registre du club (à vérifier)". Nothing of the incident stays on the device once the server has it: the projection is a stub without the text
 *  (sync/projectors.incidents.ts), and the register is read online by the direction alone, behind the second factor (Incidents.tsx). */
export function IncidentReportPage() {
  const { t } = useTranslation()
  const { send, site, kiosk, personId } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('incident.report')
  // a photo is a second fact (attachment.register@1), which the shared workstation may not send: the field is held there (the M16a review's rule)
  const photo = useWriteAccess('attachment.add')
  const areas = useAreaOptions()
  const assets = useAssets()
  const persons = usePersons(); const memberships = useMemberships()
  const team = useMemo(() => responsibleOptions(persons ?? [], memberships ?? [], site.site_id), [persons, memberships, site.site_id])
  const waiting = useIncidentsWaiting()
  const { busy, error, run } = useAction()
  const photos = usePhotoDraft()
  const id = useRef(newId())
  const [kind, setKind] = useState<IncidentKind | ''>('')
  const [severity, setSeverity] = useState<IncidentSeverity | ''>('')
  const [description, setDescription] = useState('')
  const [areaId, setAreaId] = useState('')
  const [assetId, setAssetId] = useState('')
  const [involved, setInvolved] = useState<string[]>([])
  const [when, setWhen] = useState('')
  const [sent, setSent] = useState<{ commandId: string; offline: boolean } | null>(null)

  const nowMs = Date.now()
  const whenAt = when ? localToInstant(when.slice(0, 10), when.slice(11, 16), site.timezone) : undefined
  const problem = reportProblem({ kind, severity, description, areaId, assetId, involved, ...(whenAt ? { occurredAt: whenAt } : {}) }, nowMs)
  // the machines of this site in service (the mirror's rows carry their site; the server refuses another site's: INCIDENT_PLACE_NOT_AT_SITE)
  const machines = (assets ?? []).filter((a) => a.lifecycle === 'active' && ((a as { site_id?: string }).site_id ?? site.site_id) === site.site_id)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (problem || !access.allowed || !personId) return
    void run(async () => {
      const payload = buildReportPayload(id.current, personId, { kind, severity, description, areaId, assetId, involved, ...(whenAt ? { occurredAt: whenAt } : {}) })
      const { commandId } = await send('incident.report@1', payload)
      // the evidence: ordinary photos of the incident, sent once the report is in, with no caption (the register's photo links reach no device: ADR-119)
      await photos.commit({ entity_type: 'ops.incident', entity_id: id.current }, [commandId])
      setSent({ commandId, offline: engine.online === false || (typeof navigator !== 'undefined' && navigator.onLine === false) })
    })
  }
  const another = () => { id.current = newId(); setKind(''); setSeverity(''); setDescription(''); setAreaId(''); setAssetId(''); setInvolved([]); setWhen(''); setSent(null) }
  const back = <BackLink to="/">← {t('common.back')}</BackLink>

  if (sent) return <SentView commandId={sent.commandId} offline={sent.offline} back={back} onAnother={another} />
  if (!areas) return <p>{t('common.loading')}</p>
  const length = codePoints(description.trim())
  return (
    <section className="flex flex-col gap-4" data-testid="incident-page">
      {back}
      <div>
        <h1 className="text-2xl font-bold">{t('incidents.report.title')}</h1>
        <p className="meta">{t('incidents.report.help')}</p>
      </div>
      <Banner tone="warn" testId="incident-register-line">{t('incidents.report.registerLine')}</Banner>
      {!access.permitted && <Banner tone="warn" testId="incident-not-allowed">{t('incidents.report.notAllowed')}</Banner>}
      {access.readOnlyMode && <Banner tone="warn">{t('platform.readOnly')}</Banner>}
      {waiting > 0 && <p className="font-semibold text-sand-ink dark:text-sand" data-testid="incident-waiting">{t('incidents.waiting', { count: waiting })}</p>}
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">{t('incidents.report.kind')}</legend>
          <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('incidents.report.kind')}>
            {INCIDENT_KINDS.map((k) => (
              <button key={k} type="button" className={`btn ${kind === k ? 'btn-primary' : ''}`} aria-pressed={kind === k} onClick={() => setKind(k)} data-testid={`incident-kind-${k}`}>{kindText(t, k)}</button>
            ))}
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">{t('incidents.report.severity')}</legend>
          <div className="grid grid-cols-3 gap-2" role="group" aria-label={t('incidents.report.severity')}>
            {INCIDENT_SEVERITIES.map((s) => (
              <button key={s} type="button" className={`btn ${severity === s ? 'btn-primary' : ''}`} aria-pressed={severity === s} onClick={() => setSeverity(s)} data-testid={`incident-severity-${s}`}>{severityText(t, s)}</button>
            ))}
          </div>
          <p className="meta">{t('incidents.report.severityHint')}</p>
        </fieldset>
        <Field label={t('incidents.report.description')} hint={t('incidents.report.descriptionHint', { max: INCIDENT_DESCRIPTION_MAX })} htmlFor="incident-description"
          error={problem === 'descriptionLong' ? t('incidents.report.missing.descriptionLong', { max: INCIDENT_DESCRIPTION_MAX }) : null}>
          <textarea id="incident-description" className="input" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="incident-description" />
        </Field>
        <p className="meta -mt-3 text-right" data-testid="incident-count">{t('incidents.report.count', { n: length, max: INCIDENT_DESCRIPTION_MAX })}</p>
        <Field label={t('incidents.report.area')} htmlFor="incident-area">
          <select id="incident-area" className="input" value={areaId} onChange={(e) => setAreaId(e.target.value)} data-testid="incident-area">
            <option value="">{t('incidents.report.noArea')}</option>
            {areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </Field>
        <Field label={t('incidents.report.machine')} htmlFor="incident-machine">
          <select id="incident-machine" className="input" value={assetId} onChange={(e) => setAssetId(e.target.value)} data-testid="incident-machine">
            <option value="">{t('incidents.report.noMachine')}</option>
            {machines.map((a) => <option key={a.id} value={a.id}>{machineLabel(a)}</option>)}
          </select>
        </Field>
        <details className="card" data-testid="incident-people">
          <summary className="flex min-h-14 cursor-pointer items-center font-semibold">{involved.length ? t('incidents.report.peopleChosen', { count: involved.length }) : t('incidents.report.people')}</summary>
          <p className="meta mb-2">{t('incidents.report.peopleHint')}</p>
          <ul className="flex flex-col">
            {team.map((p) => (
              <li key={p.id}>
                <label className="flex min-h-14 cursor-pointer items-center gap-3">
                  <input type="checkbox" className="size-6 shrink-0" checked={involved.includes(p.id)} data-testid="incident-person" data-id={p.id}
                    onChange={(e) => setInvolved((all) => (e.target.checked ? [...all, p.id] : all.filter((x) => x !== p.id)))} />
                  <span>{p.name}</span>
                </label>
              </li>
            ))}
          </ul>
        </details>
        <details className="card">
          <summary className="flex min-h-14 cursor-pointer items-center font-semibold" data-testid="incident-when-toggle">{t('incidents.report.when')}</summary>
          <Field label={t('incidents.report.when')} hint={t('incidents.report.whenHint')} htmlFor="incident-when" error={problem === 'when' ? t('incidents.report.missing.when') : null}>
            <input id="incident-when" className="input" type="datetime-local" value={when} max={formatLocal(nowMs, site.timezone, "yyyy-MM-dd'T'HH:mm")} onChange={(e) => setWhen(e.target.value)} data-testid="incident-when" />
          </Field>
          {when && <button type="button" className="btn mt-2" onClick={() => setWhen('')} data-testid="incident-when-clear">{t('incidents.report.whenNow')}</button>}
        </details>
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-bold">{t('incidents.report.photo')}</h2>
          <p className="meta">{t('incidents.report.photoHint')}</p>
          {kiosk && !photo.permitted && <p className="meta" data-testid="incident-photo-kiosk">{t('incidents.report.photoKiosk')}</p>}
          <PhotoField draft={photos} disabled={!access.allowed || !photo.allowed} />
        </div>
        {error && <Banner tone="error"><span data-testid="incident-error">{error}</span></Banner>}
        <button className="btn btn-primary" disabled={busy || !!problem || !access.allowed} data-testid="incident-submit">{busy ? t('common.saving') : t('incidents.report.send')}</button>
        <DisabledHint show={!!problem && access.allowed} testId="incident-hint">{missingText(t, problem)}</DisabledHint>
        <p className="meta" data-testid="incident-rule">{t('incidents.report.rule')}</p>
      </form>
    </section>
  )
}

function missingText(t: (k: string, o?: Record<string, unknown>) => string, p: ReportProblem): string {
  switch (p) {
    case 'kind': return t('incidents.report.missing.kind')
    case 'severity': return t('incidents.report.missing.severity')
    case 'description': return t('incidents.report.missing.description')
    case 'descriptionLong': return t('incidents.report.missing.descriptionLong', { max: INCIDENT_DESCRIPTION_MAX })
    case 'involvedMany': return t('incidents.report.missing.involvedMany')
    case 'when': return t('incidents.report.missing.when')
    default: return ''
  }
}

/** After the send: what the device knows of it, live (waiting on this device, received by the server, or refused), and that the text does not stay here. */
function SentView({ commandId, offline, back, onAnother }: { commandId: string; offline: boolean; back: ReactNode; onAnother: () => void }) {
  const { t } = useTranslation()
  const st = useReportState(commandId)
  return (
    <section className="flex flex-col gap-4" data-testid="incident-sent" data-state={st.state}>
      {back}
      <h1 className="text-2xl font-bold">{t('incidents.report.title')}</h1>
      {st.state === 'refused' ? <Banner tone="error"><span data-testid="incident-sent-refused">{t('incidents.report.refused', { reason: errorText(t, st.code) })}</span></Banner>
        : st.state === 'received' ? <Banner tone="ok"><span className="text-lg font-bold" data-testid="incident-sent-ok">{t('incidents.report.received')}</span></Banner>
        : <Banner tone={offline ? 'warn' : 'info'}><span className="text-lg font-bold" data-testid="incident-sent-waiting">{t('incidents.report.waitingHere')}</span></Banner>}
      <p data-testid="incident-sent-rule">{t('incidents.report.afterSend')}</p>
      <button type="button" className="btn btn-primary" onClick={onAnother} data-testid="incident-another">{t('incidents.report.another')}</button>
      <Link to="/" className="btn" data-testid="incident-home">{t('common.today')}</Link>
    </section>
  )
}
